import { useState } from "react";
import { ChevronDown, Check, Star } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "../../client/components/ui/popover";
import { cn } from "../../client/utils";
import useTranslation from "../../client/hooks/useTranslation";

// pfmTemplateFilter stores the first ID of a name group; null = all
interface TopbarPfmFilterWidgetProps {
  accounts: any[];
  pfmTemplateFilter: string | null;
  onPfmTemplateFilterChange: (id: string | null) => void;
  pinnedValue?: string | null;
  onPinValue?: (id: string | null) => void;
}

interface RowProps {
  label: string;
  isSelected: boolean;
  isPinned: boolean;
  onSelect: () => void;
  onPin?: () => void;
}

function FilterRow({ label, isSelected, isPinned, onSelect, onPin }: RowProps) {
  return (
    <div
      className={cn(
        "group flex items-center gap-1 rounded-sm px-2 py-1.5 text-sm cursor-pointer select-none",
        "hover:bg-accent hover:text-accent-foreground",
        isSelected && "bg-accent/50"
      )}
      onClick={onSelect}
    >
      <Check className={cn("h-3.5 w-3.5 shrink-0 mr-1", isSelected ? "opacity-100" : "opacity-0")} />
      <span className="flex-1 truncate">{label}</span>
      {onPin && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onPin(); }}
          className={cn(
            "flex-shrink-0 flex h-5 w-5 items-center justify-center rounded transition-colors",
            "opacity-0 group-hover:opacity-100",
            isPinned ? "opacity-100 text-yellow-400 hover:text-yellow-500" : "text-muted-foreground hover:text-yellow-400"
          )}
        >
          <Star className={cn("h-3 w-3", isPinned && "fill-yellow-400")} />
        </button>
      )}
    </div>
  );
}

export default function TopbarPfmFilterWidget({
  accounts,
  pfmTemplateFilter,
  onPfmTemplateFilterChange,
  pinnedValue,
  onPinValue,
}: TopbarPfmFilterWidgetProps) {
  const t = useTranslation();
  const [open, setOpen] = useState(false);

  // Deduplicate by name — multiple templates with same name (system vs custom) appear as one entry.
  // We keep the first ID encountered per name so the filter selects all accounts sharing that name.
  const templatesByName = new Map<string, { ids: string[]; name: string }>();
  for (const a of accounts) {
    if (!a.propFirmTemplateId || !a.propFirmTemplate?.name) continue;
    const name = a.propFirmTemplate.name as string;
    const existing = templatesByName.get(name);
    if (existing) {
      if (!existing.ids.includes(a.propFirmTemplateId)) existing.ids.push(a.propFirmTemplateId);
    } else {
      templatesByName.set(name, { ids: [a.propFirmTemplateId], name });
    }
  }
  const templates = Array.from(templatesByName.values());

  if (templates.length === 0) return null;

  const allLabel = t.dashboard.widgets.topbarPfmFilterAll;
  const triggerPlaceholder = t.dashboard.widgets.topbarPfmFilter;
  // A selection stores the first ID of the name group; match by checking if pfmTemplateFilter is in the group's IDs
  const selectedTemplate = pfmTemplateFilter
    ? templates.find((tpl) => tpl.ids.includes(pfmTemplateFilter))
    : null;
  const selectedLabel = selectedTemplate?.name ?? triggerPlaceholder;

  const select = (ids: string[] | null) => {
    onPfmTemplateFilterChange(ids ? ids[0] : null);
    setOpen(false);
  };
  const pin = onPinValue ? (ids: string[] | null) => onPinValue(ids ? ids[0] : null) : undefined;

  const isActive = pfmTemplateFilter !== null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            "flex items-center justify-between gap-2 rounded-md border px-3 font-medium text-sm transition-colors",
            isActive
              ? "bg-primary text-primary-foreground border-primary hover:bg-primary/90"
              : "bg-background text-foreground border-border hover:bg-accent hover:text-accent-foreground",
            "w-[200px] h-9"
          )}
        >
          <span className="truncate">{selectedLabel}</span>
          <ChevronDown className="h-4 w-4 shrink-0 opacity-70" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[220px] p-1 max-h-[400px] overflow-y-auto" align="start">
        <FilterRow
          label={allLabel}
          isSelected={pfmTemplateFilter === null}
          isPinned={pinnedValue !== undefined && pinnedValue === null}
          onSelect={() => select(null)}
          onPin={pin ? () => pin(null) : undefined}
        />
        {templates.map((tpl) => (
          <FilterRow
            key={tpl.ids[0]}
            label={tpl.name}
            isSelected={pfmTemplateFilter !== null && tpl.ids.includes(pfmTemplateFilter)}
            isPinned={pinnedValue !== null && pinnedValue !== undefined && tpl.ids.includes(pinnedValue)}
            onSelect={() => select(tpl.ids)}
            onPin={pin ? () => pin(tpl.ids) : undefined}
          />
        ))}
      </PopoverContent>
    </Popover>
  );
}
